import { useEffect, useMemo } from 'react';
import {
  LocationFeatureResponse,
  LocationsFeaturesResponse,
} from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQueries } from 'react-query';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { FeatureFlagQueries } from '@frontend/api-feature-flags';
import { useHasAclAccess } from '@frontend/analytics-shared';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import {
  useAppFlagStore,
  useAppFlagStoreWithSetters,
  VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS,
  MESSAGING_AIR_CUSTOMIZATION_FEATURE_ENUMS,
} from '@frontend/shared';
import { AIR_FEATURE_FLAGS } from '../constants';

// Customization flags to check (OR logic - location passes if ANY of these are active)
const AI_RECEPTIONIST_CUSTOMIZATION_FLAGS = [
  ...VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS,
  ...MESSAGING_AIR_CUSTOMIZATION_FEATURE_ENUMS,
];

type Props = {
  isAllAccessScopeEnabled?: boolean;
  isDemoAccount?: boolean;
};

type Response = {
  hasAccessToMultipleLocations?: boolean;
  isLoading?: boolean;
  locationIds: string[];
  locationNames: Record<string, string>;
  preselectedAccessibleLocationIds: string[];
  isVoiceAirEnabled?: boolean;
  isMessagingAirEnabled?: boolean;
};

/**
 * Build a map of locationId -> LocationFeatureResponse for AI receptionist customization flags.
 * A location is included if it has AIR customization flags in ACTIVE/PROMOTE state.
 */
const buildCustomizationFlagMapFromBatched = (
  data: LocationsFeaturesResponse | undefined,
  allowedIds: string[]
): Record<string, LocationFeatureResponse | undefined> => {
  if (!data) return {};

  const byLocation = data.locationsFeatures ?? {};
  const allowedSet = new Set(allowedIds);
  const flagMap: Record<string, LocationFeatureResponse | undefined> = {};

  const activeStates = [
    CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
    CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
  ];

  Object.entries(byLocation).forEach(([locationId, flagsData]) => {
    if (!allowedSet.has(locationId)) return;

    const features = flagsData?.features ?? [];
    // Find any matching AI receptionist customization flag that is in ACTIVE/PROMOTE state
    const match = features.find((f) => {
      const isMatchingFlag = AI_RECEPTIONIST_CUSTOMIZATION_FLAGS.some((flagName) => f?.featureEnum === flagName);
      const isActiveState = f?.state && activeStates.includes(f.state);
      return isMatchingFlag && isActiveState;
    });

    if (match) {
      flagMap[locationId] = match;
    }
  });

  return flagMap;
};

/**
 * Get location IDs from the flag map.
 * Since buildCustomizationFlagMapFromBatched already filters by active state,
 * this function just extracts the location IDs.
 */
const getActiveLocationIdsFromFlagMap = (flagMap: Record<string, LocationFeatureResponse | undefined>): string[] => {
  return Object.keys(flagMap);
};

/**
 * Check if a location has any AI receptionist customization flag active in the store.
 */
const hasActiveCustomizationFlagInStore = (
  customizationFlags: Record<string, Map<string, LocationFeatureResponse>>,
  locationId: string
): boolean => {
  const locationFlags = customizationFlags[locationId];
  if (!locationFlags) return false;

  return AI_RECEPTIONIST_CUSTOMIZATION_FLAGS.some((flagName) => {
    const flag = locationFlags.get(flagName);
    return (
      flag?.state &&
      [
        CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
        CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
      ].includes(flag.state)
    );
  });
};

/**
 * Hook to fetch and manage AI receptionist locations.
 * Filters locations by:
 * 1. ACL permission (ANALYTICS_READ)
 * 2. Customization flags (AIR customization flags must be active)
 * Fetches flags only for locations not already cached in the store.
 */
export const useAiReceptionistOrgLocations = ({
  isAllAccessScopeEnabled = false,
  isDemoAccount = false,
}: Props): Response => {
  const { getScopeName, selectedLocationIds = [] } = useAppScopeStore();
  const { featureFlags, customizationFlags } = useAppFlagStore();
  const { setFeatureFlags, setCustomizationFlags } = useAppFlagStoreWithSetters();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  // Step 1: Get locations with ACL access
  const { locationIdsWithAclAccess } = useHasAclAccess({
    isAllAccessScopeEnabled,
    isDemoAccount,
    permissions: [Permission.ANALYTICS_READ],
  });

  // Step 2: Determine which locations need customization flag fetching
  const uncachedCustomizationFlagLocationIds = useMemo(() => {
    if (!isAllAccessScopeEnabled) return []; // For scoped mode, flags are already in store
    return locationIdsWithAclAccess.filter((locationId) => !customizationFlags[locationId]);
  }, [isAllAccessScopeEnabled, locationIdsWithAclAccess, customizationFlags]);

  // Step 3: Fetch customization flags for uncached locations
  const { data: customizationFlagsData, isLoading: isCustomizationFlagsLoading } =
    CustomizationFlagQueries.useBatchedMultiCustomizationFlags(uncachedCustomizationFlagLocationIds, {
      enabled: uncachedCustomizationFlagLocationIds.length > 0,
    });

  // Persist fetched customization flags to store
  useEffect(() => {
    if (customizationFlagsData?.locationsFeatures) {
      Object.entries(customizationFlagsData.locationsFeatures).forEach(([locationId, flagsData]) => {
        setCustomizationFlags({ locationId, flags: flagsData?.features || [] });
      });
    }
  }, [customizationFlagsData, setCustomizationFlags]);

  /**
   * Fetch air demo mode feature flag value for the current location to determine
   * if we should bypass customization flag checks and show all locations with ACL access.
   */
  const isDemoModeFlagActive = getFeatureFlagValue(AIR_FEATURE_FLAGS.DEMO_MODE);

  const showDemoData = useMemo(() => isDemoModeFlagActive || isDemoAccount, [isDemoModeFlagActive, isDemoAccount]);

  // Step 4: Build list of locations with active customization flags
  const locationsWithActiveCustomizationFlags = useMemo(() => {
    // If demo mode is active, bypass all checks and return all locations with ACL access
    if (showDemoData) {
      return locationIdsWithAclAccess;
    }

    if (!isAllAccessScopeEnabled) {
      // For scoped mode, check from store directly
      return locationIdsWithAclAccess.filter((locationId) =>
        hasActiveCustomizationFlagInStore(customizationFlags, locationId)
      );
    }

    // For all-access mode, combine cached and newly fetched
    const cachedActiveLocations = locationIdsWithAclAccess.filter(
      (locationId) =>
        customizationFlags[locationId] && hasActiveCustomizationFlagInStore(customizationFlags, locationId)
    );

    const newlyFetchedFlagMap = buildCustomizationFlagMapFromBatched(
      customizationFlagsData,
      uncachedCustomizationFlagLocationIds
    );
    const newlyFetchedActiveLocations = getActiveLocationIdsFromFlagMap(newlyFetchedFlagMap);

    return [...new Set([...cachedActiveLocations, ...newlyFetchedActiveLocations])];
  }, [
    isAllAccessScopeEnabled,
    locationIdsWithAclAccess,
    customizationFlags,
    customizationFlagsData,
    uncachedCustomizationFlagLocationIds,
    showDemoData,
  ]);

  // Step 5: Determine which locations need feature flag fetching
  const uncachedFeatureFlagLocationIds = useMemo(() => {
    if (!isAllAccessScopeEnabled) return []; // For scoped mode, flags are already in store
    return locationsWithActiveCustomizationFlags.filter((locationId) => !featureFlags[locationId]);
  }, [isAllAccessScopeEnabled, locationsWithActiveCustomizationFlags, featureFlags]);

  // Step 6: Fetch feature flags for uncached locations
  const featureFlagQueries = useQueries(
    uncachedFeatureFlagLocationIds.map((locationId) => ({
      queryKey: FeatureFlagQueries.featureFlagQueryKeys.location(locationId),
      queryFn: () => FeatureFlagQueries.getLocationFeatureFlags({ locationId }),
      enabled: uncachedFeatureFlagLocationIds.length > 0,
    }))
  );

  const isFeatureFlagsLoading = featureFlagQueries.some((query) => query.isLoading);
  const featureFlagQueriesReady = featureFlagQueries.every((query) => query.isSuccess);

  // Persist fetched feature flags to store
  useEffect(() => {
    if (featureFlagQueriesReady && uncachedFeatureFlagLocationIds.length > 0) {
      featureFlagQueries.forEach((query, index) => {
        const locationId = uncachedFeatureFlagLocationIds[index];
        if (query.data?.flags) {
          setFeatureFlags({ locationId, flags: query.data.flags });
        }
      });
    }
  }, [featureFlagQueriesReady, uncachedFeatureFlagLocationIds.length]);

  // Step 7: Filter locations by feature flag + specific customization flags (voice & messaging split)

  const locationsWithVoiceAIRActive = useMemo(() => {
    return locationsWithActiveCustomizationFlags.filter((locationId) => {
      const locationFlags = customizationFlags[locationId];
      const locationFeatureFlags = featureFlags[locationId];

      if (!locationFlags || !locationFeatureFlags) return false;

      // If demo mode feature flag is active, bypass checks and activate voice air
      const isDemoModeFeatureFlagActive = locationFeatureFlags?.get(AIR_FEATURE_FLAGS.DEMO_MODE)?.value === true;
      if (isDemoModeFeatureFlagActive) return true;

      const isFlagActive = (flag?: LocationFeatureResponse) =>
        flag?.state
          ? [
              CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
              CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
            ].includes(flag.state)
          : false;

      const hasVoiceCF =
        isFlagActive(locationFlags.get(Feature.TRUELARK_AI_VOICE)) ||
        isFlagActive(locationFlags.get(Feature.WEAVE_VOICE_AIR));

      return hasVoiceCF;
    });
  }, [locationsWithActiveCustomizationFlags, customizationFlags, featureFlags]);

  const locationsWithMessagingAIRActive = useMemo(() => {
    return locationsWithActiveCustomizationFlags.filter((locationId) => {
      const locationFlags = customizationFlags[locationId];
      const locationFeatureFlags = featureFlags[locationId];

      if (!locationFlags || !locationFeatureFlags) return false;

      // If demo mode feature flag is active, bypass checks and activate messaging air
      const isDemoModeFeatureFlagActive = locationFeatureFlags?.get(AIR_FEATURE_FLAGS.DEMO_MODE)?.value === true;
      if (isDemoModeFeatureFlagActive) return true;

      const isFlagActive = (flag?: LocationFeatureResponse) =>
        flag?.state
          ? [
              CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
              CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
            ].includes(flag.state)
          : false;

      const hasMessagingCF =
        isFlagActive(locationFlags.get(Feature.TRUELARK_AI_MESSAGING)) ||
        isFlagActive(locationFlags.get(Feature.WEAVE_MESSAGING_AIR));

      return hasMessagingCF;
    });
  }, [locationsWithActiveCustomizationFlags, customizationFlags, featureFlags]);

  const locationIds = useMemo(() => {
    return Array.from(new Set([...locationsWithVoiceAIRActive, ...locationsWithMessagingAIRActive]));
  }, [locationsWithVoiceAIRActive, locationsWithMessagingAIRActive]);

  // Step 8: Build location names map
  const locationNames = useMemo(() => {
    return locationIds.reduce<Record<string, string>>((acc, locationId) => {
      acc[locationId] = getScopeName(locationId);
      return acc;
    }, {});
  }, [locationIds, getScopeName]);

  // Step 9: Filter to preselected accessible locations (intersection with GLS selection)
  const preselectedAccessibleLocationIds = useMemo(() => {
    const locationIdSet = new Set(locationIds);
    return selectedLocationIds.filter((id) => locationIdSet.has(id));
  }, [locationIds, selectedLocationIds]);

  const hasAccessToMultipleLocations = useMemo(
    () => isAllAccessScopeEnabled && locationIds.length > 1,
    [isAllAccessScopeEnabled, locationIds.length]
  );

  const isLoading = isCustomizationFlagsLoading || isFeatureFlagsLoading;

  return {
    hasAccessToMultipleLocations,
    isLoading,
    locationIds,
    locationNames,
    isMessagingAirEnabled: isDemoModeFlagActive || locationsWithMessagingAIRActive?.length > 0,
    isVoiceAirEnabled: isDemoModeFlagActive || locationsWithVoiceAIRActive?.length > 0,
    preselectedAccessibleLocationIds,
  };
};
